import { BackgroundColor, InAppBrowser, ToolBarType } from '@capgo/capacitor-inappbrowser';
import { Capacitor } from '@capacitor/core';

import { clientDebugLog } from './clientDebugLog';
import { isNativeOAuthCallbackUrl } from './keycloakRedirect';

let oauthBrowserListenersRegistered = false;
let oauthRedirectReceived = false;
let activeOAuthWebViewId: string | undefined;

/**
 * Opens Keycloak OAuth in Capgo's managed WebView.
 * Use an HTTPS redirect URI (`VITE_HUB_URL/login`) so `urlChangeEvent` can intercept the callback.
 * Custom schemes cannot load inside a WebView (ERR_UNKNOWN_URL_SCHEME).
 */
export async function openOAuthWebView(url: string): Promise<void> {
	const result = await InAppBrowser.openWebView({
		url,
		title: 'Sign in',
		toolbarType: ToolBarType.COMPACT,
		backgroundColor: BackgroundColor.WHITE,
		toolbarColor: '#ffffff',
		visibleTitle: true,
		showArrow: false,
		activeNativeNavigationForWebview: true,
		isPresentAfterPageLoad: false,
		preventDeeplink: false,
	});

	activeOAuthWebViewId = result.id;
}

/** True when an OAuth WebView was opened and has not been closed yet. */
export function hasActiveOAuthWebView(): boolean {
	return activeOAuthWebViewId !== undefined;
}

/**
 * Closes the active OAuth WebView.
 * No-ops when none is open — Capgo `close()` without a WebView can pause/resume
 * the host Activity and loop with `appStateChange` resume guards.
 */
export async function closeOAuthWebView(): Promise<void> {
	const webViewId = activeOAuthWebViewId;

	if (!webViewId) {
		return;
	}

	activeOAuthWebViewId = undefined;

	try {
		await InAppBrowser.close({ id: webViewId });
	} catch {
		// Browser may already be closed when OAuth completes.
	}
}

/** Marks that an OAuth redirect was handled (suppresses closeEvent failure). */
export function markOAuthRedirectReceived(): void {
	oauthRedirectReceived = true;
}

/** Resets redirect tracking before starting a new OAuth flow. */
export function resetOAuthRedirectTracking(): void {
	oauthRedirectReceived = false;
}

export type OAuthBrowserListenerOptions = {
	isLoginInProgress: () => boolean;
	onOAuthRedirect: (url: string) => void | Promise<void>;
	onBrowserClosedWithoutAuth: () => void;
};

/**
 * Registers Capgo InAppBrowser listeners for HTTPS OAuth redirect interception
 * (`urlChangeEvent`) and abandoned-login handling (`closeEvent`).
 */
export function registerOAuthBrowserListeners(options: OAuthBrowserListenerOptions): void {
	if (!Capacitor.isNativePlatform() || oauthBrowserListenersRegistered) {
		return;
	}

	oauthBrowserListenersRegistered = true;

	void InAppBrowser.addListener('urlChangeEvent', (event) => {
		const redirectUrl = event.url;

		clientDebugLog('log', '[OAuthDebug] urlChangeEvent', {
			hypothesisId: 'A',
			url: redirectUrl ?? null,
			isNativeCallback: redirectUrl ? isNativeOAuthCallbackUrl(redirectUrl) : false,
		});

		if (!redirectUrl || !isNativeOAuthCallbackUrl(redirectUrl)) {
			return;
		}

		oauthRedirectReceived = true;

		void (async () => {
			await closeOAuthWebView();
			await options.onOAuthRedirect(redirectUrl);
		})();
	});

	void InAppBrowser.addListener('browserPageLoaded', () => {
		clientDebugLog('log', '[OAuthDebug] browserPageLoaded', {
			hypothesisId: 'B',
			loginInProgress: options.isLoginInProgress(),
		});
	});

	void InAppBrowser.addListener('closeEvent', () => {
		clientDebugLog('log', '[OAuthDebug] closeEvent', {
			hypothesisId: 'C',
			loginInProgress: options.isLoginInProgress(),
			oauthRedirectReceived,
		});

		activeOAuthWebViewId = undefined;

		if (!options.isLoginInProgress()) {
			return;
		}

		if (oauthRedirectReceived) {
			return;
		}

		options.onBrowserClosedWithoutAuth();
	});
}

/** Resets listener registration state for tests. */
export function resetOAuthBrowserForTests(): void {
	oauthBrowserListenersRegistered = false;
	oauthRedirectReceived = false;
	activeOAuthWebViewId = undefined;
}
